/**
 * Axios instance configured for API calls with proper authentication handling.
 * This ensures cookies are sent with all requests and handles 401 errors gracefully.
 */

import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true, // CRITICAL: Include cookies in all requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for handling auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle network errors (no response from server)
    if (!error.response) {
      console.error('Network error:', error.message);
      // Don't show "Network Error" - give a more user-friendly message
      error.userMessage = 'Unable to connect to the server. Please check your internet connection.';
      return Promise.reject(error);
    }

    const { status } = error.response;

    // Handle 401 Unauthorized
    if (status === 401) {
      console.warn('Session expired or invalid. User needs to re-authenticate.');
      
      // Check if we're not already on the landing page to avoid redirect loop
      if (window.location.pathname !== '/') {
        // Clear any stale state and redirect to login
        // Use a slight delay to allow any pending operations to complete
        setTimeout(() => {
          window.location.href = '/';
        }, 100);
      }
      
      error.userMessage = 'Your session has expired. Please log in again.';
      return Promise.reject(error);
    }

    // Handle 403 Forbidden (unauthorized email)
    if (status === 403) {
      error.userMessage = error.response.data?.detail || 'You are not authorized to access this application.';
      return Promise.reject(error);
    }

    // Handle other errors
    if (error.response.data?.detail) {
      error.userMessage = error.response.data.detail;
    }

    return Promise.reject(error);
  }
);

// Request interceptor to handle any pre-request setup
api.interceptors.request.use(
  (config) => {
    // Log requests in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default api;

// Export convenience methods that mirror axios API
export const get = (url, config) => api.get(url, config);
export const post = (url, data, config) => api.post(url, data, config);
export const put = (url, data, config) => api.put(url, data, config);
export const del = (url, config) => api.delete(url, config);
export const patch = (url, data, config) => api.patch(url, data, config);
